import { Link } from 'react-router-dom';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

export const Home = () => {
  const { isFeatureEnabled } = useFeatureFlag();
  
  // Check if we're on www.finderz.ai or finderz.ai (not app.finderz.ai)
  const isMainDomain = window.location.hostname === 'www.finderz.ai' || window.location.hostname === 'finderz.ai';
  
  return (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
    {/* Navigation */}
    <nav className="relative z-10 flex items-center justify-between p-6 lg:px-8">
      <div className="flex items-center">
        <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <span className="ml-3 text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
          Finderz AI
        </span>
      </div>
      <div className="flex items-center gap-6">
        <Link 
          to="/our-story" 
          className="text-gray-700 hover:text-blue-600 transition duration-300 font-medium"
        >
          Our Story
        </Link>
        <div className="flex items-center gap-4">
          {isMainDomain ? (
            <>
              <a 
                href="https://app.finderz.ai/login" 
                className="text-gray-700 hover:text-blue-600 transition duration-300 font-medium"
              >
                Sign In
              </a>
              <a 
                href="https://app.finderz.ai/register" 
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2.5 rounded-xl hover:shadow-lg transform hover:scale-105 transition duration-300 font-medium"
              >
                Get Started
              </a>
            </>
          ) : (
            <>
              <Link 
                to="/login" 
                className="text-gray-700 hover:text-blue-600 transition duration-300 font-medium"
              >
                Sign In
              </Link>
              <Link 
                to="/register" 
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2.5 rounded-xl hover:shadow-lg transform hover:scale-105 transition duration-300 font-medium"
              >
                Get Started
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>

    {/* Hero Section */}
    <div className="relative px-6 lg:px-8">
      <div className="mx-auto max-w-7xl pt-20 pb-32 sm:pt-32 sm:pb-40">
        <div className="text-center">
          {isFeatureEnabled('NEW_LANDING_CONTENT') ? (
            <>
              <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl lg:text-7xl">
                Agentic AI Designing & Building
                <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  Data Centers
                </span>
              </h1>
              <p className="mt-6 text-lg leading-8 text-gray-600 max-w-3xl mx-auto">
                Plan and build data centers faster, safer, greener.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl lg:text-7xl">
                Project Management
                <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  Made Simple
                </span>
              </h1>
              <p className="mt-6 text-lg leading-8 text-gray-600 max-w-2xl mx-auto">
                Transform your project workflow with Finderz AI. Track progress, manage teams, 
                and deliver results faster than ever before. Everything you need in one AI-powered platform.
              </p>
            </>
          )}
          <div className="mt-10 flex items-center justify-center gap-x-6">
            {isMainDomain ? (
              <a
                href="https://app.finderz.ai/register"
                className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-4 text-lg font-semibold text-white shadow-lg hover:shadow-xl rounded-2xl transform hover:scale-105 transition duration-300"
              >
                Start Free Trial
              </a>
            ) : (
              <Link
                to="/register"
                className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-4 text-lg font-semibold text-white shadow-lg hover:shadow-xl rounded-2xl transform hover:scale-105 transition duration-300"
              >
                Start Free Trial
              </Link>
            )}
            {isMainDomain ? (
              <a
                href="https://app.finderz.ai/login"
                className="text-lg font-semibold text-gray-900 hover:text-blue-600 transition duration-300 flex items-center gap-2"
              >
                Watch Demo
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m6-6a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </a>
            ) : (
              <Link
                to="/login"
                className="text-lg font-semibold text-gray-900 hover:text-blue-600 transition duration-300 flex items-center gap-2"
              >
                Watch Demo
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m6-6a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* Features Section */}
    <div className="py-24 bg-white/50 backdrop-blur-sm">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          {isFeatureEnabled('NEW_LANDING_CONTENT') ? (
            <>
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                From sketch to construction — just prompt AI
              </h2>
              <p className="mt-6 text-lg leading-8 text-gray-600">
                Keep every project aligned with your datacenter standards. Add context from CAD/BIM libraries 
                to stay consistent with your design and compliance rules.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                Everything you need to succeed
              </h2>
              <p className="mt-6 text-lg leading-8 text-gray-600">
                Powerful features designed to streamline your workflow and boost productivity.
              </p>
            </>
          )}
        </div>
        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
            {isFeatureEnabled('NEW_LANDING_CONTENT') ? (
              <>
                <div className="flex flex-col bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition duration-300">
                  <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                    <div className="h-10 w-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                      <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    From idea to build with real data
                  </dt>
                  <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">
                      Import layouts, vendor specs, and regulatory constraints, and let Finderz AI generate a construction plan that integrates whitespace fit-out with gray-space MEP systems.
                    </p>
                  </dd>
                </div>
                <div className="flex flex-col bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition duration-300">
                  <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                    <div className="h-10 w-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                      <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    Work side-by-side with AI
                  </dt>
                  <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">
                      Review the construction plan AI generates and make quick changes — move racks, update cabling paths, or fine-tune cooling zones.
                    </p>
                  </dd>
                </div>
                <div className="flex flex-col bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition duration-300">
                  <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                    <div className="h-10 w-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                      <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    AI to refine layouts, optimize systems
                  </dt>
                  <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">
                      From there, apply custom parameters and let Finderz AI turn concepts into build-ready plans and produce build-ready designs.
                    </p>
                  </dd>
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-col bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition duration-300">
                  <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                    <div className="h-10 w-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                      <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    Project Tracking
                  </dt>
                  <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">
                      Real-time project monitoring with advanced analytics and reporting. Track progress, milestones, and team performance effortlessly.
                    </p>
                  </dd>
                </div>
                <div className="flex flex-col bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition duration-300">
                  <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                    <div className="h-10 w-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                      <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 515.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    Team Collaboration
                  </dt>
                  <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">
                      Seamless team communication with integrated chat, file sharing, and collaborative workspaces for maximum productivity.
                    </p>
                  </dd>
                </div>
                <div className="flex flex-col bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition duration-300">
                  <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                    <div className="h-10 w-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                      <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    Smart Automation
                  </dt>
                  <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">
                      Automate repetitive tasks with intelligent workflows. Set up triggers, notifications, and streamline your processes.
                    </p>
                  </dd>
                </div>
              </>
            )}
          </dl>
        </div>
      </div>
    </div>

    {/* Team Story Section - Only for new landing content */}
    {isFeatureEnabled('NEW_LANDING_CONTENT') && (
      <div className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Built by data center experts
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Our team brings decades of combined experience in data center development, 
              cybersecurity, and AI innovation to create the most advanced construction planning platform.
            </p>
          </div>
          
          <div className="mx-auto mt-16 max-w-5xl">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-16 items-center">
              {/* Timeline */}
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">2024 - The Idea</h3>
                    <p className="mt-2 text-gray-600">
                      Drawing from our extensive experience in data centers, development, cybersecurity, and AI, 
                      we conceived a revolutionary approach to construction planning.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">2025 - Launch</h3>
                    <p className="mt-2 text-gray-600">
                      We launched Finderz AI, combining our deep domain expertise with cutting-edge AI 
                      to transform how data centers are designed and built.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Expertise Areas */}
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-lg">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Data Centers</h4>
                  <p className="text-sm text-gray-600">Years of experience in data center design, construction, and operations</p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-lg">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Development</h4>
                  <p className="text-sm text-gray-600">Deep technical expertise in software architecture and system design</p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-lg">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Cybersecurity</h4>
                  <p className="text-sm text-gray-600">Enterprise-grade security practices and infrastructure protection</p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-lg">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">AI Innovation</h4>
                  <p className="text-sm text-gray-600">Cutting-edge AI research and practical implementation experience</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* CTA Section */}
    <div className="bg-gradient-to-r from-blue-600 to-indigo-600">
      <div className="px-6 py-24 sm:px-6 sm:py-32 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          {isFeatureEnabled('NEW_LANDING_CONTENT') ? (
            <>
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Ready to build your data center with AI?
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-blue-100">
                Join the future of data center construction. From sketch to reality, 
                let AI turn your ideas into build-ready plans.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Ready to transform your workflow?
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-blue-100">
                Join thousands of teams who trust Finderz to deliver their projects on time and within budget.
              </p>
            </>
          )}
          <div className="mt-10 flex items-center justify-center gap-x-6">
            {isMainDomain ? (
              <a
                href="https://app.finderz.ai/register"
                className="bg-white px-8 py-4 text-lg font-semibold text-blue-600 shadow-lg hover:shadow-xl rounded-2xl transform hover:scale-105 transition duration-300"
              >
                Start Your Free Trial
              </a>
            ) : (
              <Link
                to="/register"
                className="bg-white px-8 py-4 text-lg font-semibold text-blue-600 shadow-lg hover:shadow-xl rounded-2xl transform hover:scale-105 transition duration-300"
              >
                Start Your Free Trial
              </Link>
            )}
            {isMainDomain ? (
              <a
                href="https://app.finderz.ai/login"
                className="text-lg font-semibold text-white hover:text-blue-100 transition duration-300"
              >
                Sign In <span aria-hidden="true">→</span>
              </a>
            ) : (
              <Link
                to="/login"
                className="text-lg font-semibold text-white hover:text-blue-100 transition duration-300"
              >
                Sign In <span aria-hidden="true">→</span>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* Footer */}
    <footer className="bg-white/80 backdrop-blur-sm">
      <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8">
        <div className="text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center shadow-lg">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <span className="ml-2 text-lg font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Finderz AI
            </span>
          </div>
          <p className="text-gray-600 text-sm max-w-md mx-auto mb-6">
            {isFeatureEnabled('NEW_LANDING_CONTENT') 
              ? 'AI for the full lifecycle of data center construction.'
              : 'Transform your project workflow with AI-powered management tools.'}
          </p>
          <p className="text-xs text-gray-500">
            &copy; 2025 Finderz AI. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  </div>
  );
};
